import React, { useEffect, useRef, useState } from "react";
import { Send, Bot, User as UserIcon, Loader2, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, apiGet } from "@/lib/api";
import ChatUsageBadge from "@/components/common/ChatUsageBadge";

const SUGGESTED_QUESTIONS = [
  "Is {SYM} fairly valued right now?",
  "What are the main risks of investing in {SYM}?",
  "How does {SYM} compare to other stocks in its sector?",
  "What did the latest annual report say about dividends?",
  "Should I look at voting or non-voting shares?",
  "What is the Graham Number and what does it mean for {SYM}?",
];

export const StockAIChatTab = ({ symbol, companyName }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [model, setModel] = useState(null);
  const [usage, setUsage] = useState(null);
  const scrollRef = useRef(null);

  // Load history + usage on mount
  useEffect(() => {
    if (!symbol) return;
    let active = true;
    apiGet(`/stocks/${encodeURIComponent(symbol)}/chat/history`)
      .then((d) => {
        if (!active) return;
        setSessionId(d?.session_id || null);
        setMessages((d?.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
          ts: m.created_at,
        })));
      })
      .catch(() => {});
    apiGet(`/stocks/chat/usage`).then((u) => active && setUsage(u)).catch(() => {});
    return () => { active = false; };
  }, [symbol]);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (textOverride) => {
    const question = (textOverride ?? input).trim();
    if (!question || busy) return;
    setMessages((prev) => [...prev, { role: "user", content: question }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post(`/stocks/${encodeURIComponent(symbol)}/chat`, {
        question,
        session_id: sessionId,
      }, { timeout: 120000 });
      setMessages((prev) => [...prev, { role: "assistant", content: data.answer }]);
      if (data?.session_id) setSessionId(data.session_id);
      if (data?.model) setModel(data.model);
      if (data?.usage) setUsage(data.usage);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail?.error === "rate_limit_exceeded") {
        const msg = detail.message || "Daily AI chat limit reached.";
        toast.error(msg, {
          action: {
            label: "Upgrade",
            onClick: () => { window.location.href = "/pricing"; },
          },
        });
        setMessages((prev) => [...prev, {
          role: "assistant",
          content: `\u26a0 ${msg}`,
          error: true,
          rateLimit: true,
        }]);
        setUsage({ ...detail, used: detail.used + 1, remaining: 0, unlimited: false });
      } else {
        const safeMsg = (typeof detail === "string" ? detail : detail?.message) || err?.message || "Chat failed";
        toast.error(safeMsg);
        setMessages((prev) => [...prev, { role: "assistant", content: `\u26a0 ${safeMsg}`, error: true }]);
      }
    } finally {
      setBusy(false);
    }
  };

  const ticker = (symbol || "").split(".")[0] || symbol;

  return (
    <div className="space-y-3" data-testid="stock-ai-chat-tab">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <Sparkles className="h-4 w-4 text-primary" />
            Ask Claude about {ticker}
            {model && <Badge variant="outline" className="text-[10px]">{model.includes("haiku") ? "Haiku" : "Sonnet"}</Badge>}
            <ChatUsageBadge usage={usage} className="ml-auto" />
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Ask questions about this stock's fundamentals, valuation, reports, or news. Answers cite the data we have on file.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {messages.length === 0 && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground">Try asking:</div>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_QUESTIONS.map((q) => {
                  const text = q.replaceAll("{SYM}", ticker);
                  return (
                    <button
                      key={q}
                      onClick={() => send(text)}
                      disabled={busy}
                      className="text-xs px-3 py-1.5 rounded-full border border-border bg-muted/40 hover:bg-muted transition-colors disabled:opacity-50"
                      data-testid={`suggested-q-${q.slice(0, 20)}`}
                    >
                      {text}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div
            ref={scrollRef}
            className="space-y-3 max-h-[480px] overflow-y-auto px-1 py-1"
            data-testid="chat-message-list"
          >
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                  {m.role === "user" ? <UserIcon className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                </div>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === "user"
                    ? "bg-primary/10 text-foreground"
                    : m.error
                      ? "bg-destructive/10 text-destructive"
                      : "bg-card border border-border"
                }`} data-testid={`chat-${m.role}-${i}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex gap-2 items-center text-sm text-muted-foreground" data-testid="chat-loading">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
                  <Bot className="h-3.5 w-3.5" />
                </div>
                <Loader2 className="h-4 w-4 animate-spin" />
                Thinking…
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2 border-t border-border">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`Ask about ${ticker}…`}
              rows={1}
              className="resize-none min-h-[40px] flex-1"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              disabled={busy}
              data-testid="chat-input"
            />
            <Button onClick={() => send()} disabled={busy || !input.trim()} data-testid="chat-send-button">
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="text-[11px] text-muted-foreground italic">
              Educational info only. Not investment advice. CSE shares are CGT-free; dividends taxed at 10% WHT.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StockAIChatTab;
